import { useState } from "react";
import { MoreVertical, Pencil, Trash2, Lock, Flag, ShieldAlert } from "lucide-react";
import { ReportContentDialog } from "./ReportContentDialog";
import { RevealAuthorDialog } from "./RevealAuthorDialog";
import { useAuth } from "../App";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

// Universal ⋮ menu: Edit/Delete when `canManage` (backend can_manage), otherwise Report.
export const ContentActionsMenu = ({
  testIdPrefix,
  canManage = true,
  reportType,
  reportId,
  revealType,
  revealId,
  onEdit,
  onDelete,
  editLocked = false,
  editLockedLabel = "Edit locked",
  deleteTitle = "Delete this post?",
  deleteDescription = "This permanently removes it along with any replies, comments and attachments. This cannot be undone.",
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const canReport = !canManage && !!reportType;
  const { user } = useAuth();
  const canReveal = !!revealType && user?.admin_role === "founder";
  const [revealOpen, setRevealOpen] = useState(false);

  const runDelete = async () => {
    setBusy(true);
    try { await onDelete(); setConfirmOpen(false); } finally { setBusy(false); }
  };

  if (!canManage && !canReport) return null;

  return (
    <div className="relative inline-block shrink-0" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Post options"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-[#64748B] hover:bg-[#F0ECF7] hover:text-[#6B4E8A] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#6B4E8A]/40"
            data-testid={`${testIdPrefix}-menu`}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[180px] rounded-xl border-[#E5DFEC] p-1" data-testid={`${testIdPrefix}-menu-content`}>
          {canReport && (
            <DropdownMenuItem
              onSelect={() => setReportOpen(true)}
              className="gap-2 text-[#B45309] focus:text-[#B45309] focus:bg-[#FFF7ED] cursor-pointer rounded-lg"
              data-testid={`${testIdPrefix}-report`}
            >
              <Flag className="w-3.5 h-3.5" /> Report
            </DropdownMenuItem>
          )}
          {canManage && onEdit && (
            <DropdownMenuItem
              disabled={editLocked}
              onSelect={() => !editLocked && onEdit()}
              className="gap-2 text-[#475569] cursor-pointer rounded-lg"
              data-testid={`${testIdPrefix}-edit`}
            >
              {editLocked ? <Lock className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
              {editLocked ? editLockedLabel : "Edit"}
            </DropdownMenuItem>
          )}
          {canReveal && (
            <DropdownMenuItem
              onSelect={() => setRevealOpen(true)}
              className="gap-2 text-[#B45309] focus:text-[#B45309] focus:bg-[#FFF7ED] cursor-pointer rounded-lg"
              data-testid={`${testIdPrefix}-reveal`}
            >
              <ShieldAlert className="w-3.5 h-3.5" /> Reveal author
            </DropdownMenuItem>
          )}
          {canManage && onEdit && <DropdownMenuSeparator className="bg-[#E5DFEC]" />}
          {canManage && <DropdownMenuItem
            onSelect={() => setConfirmOpen(true)}
            className="gap-2 text-[#EF4444] focus:text-[#EF4444] focus:bg-[#FEE2E2] cursor-pointer rounded-lg"
            data-testid={`${testIdPrefix}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      {canReveal && (
        <RevealAuthorDialog open={revealOpen} onOpenChange={setRevealOpen} contentType={revealType} contentId={revealId} testIdPrefix={testIdPrefix} />
      )}
      {canReport && (
        <ReportContentDialog open={reportOpen} onOpenChange={setReportOpen} contentType={reportType} contentId={reportId} testIdPrefix={testIdPrefix} />
      )}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`${testIdPrefix}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>{deleteTitle}</AlertDialogTitle>
            <AlertDialogDescription>{deleteDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid={`${testIdPrefix}-delete-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runDelete(); }}
              disabled={busy}
              className="rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white"
              data-testid={`${testIdPrefix}-delete-confirm`}
            >
              {busy ? "Deleting..." : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const EditedLabel = ({ editedAt, testId }) => {
  if (!editedAt) return null;
  const d = new Date(editedAt);
  const stamp = d.toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return (
    <span className="text-[11px] italic text-[#64748B]" title={d.toLocaleString("en-CA")} data-testid={testId}>
      Edited · {stamp}
    </span>
  );
};

export default ContentActionsMenu;
